/**
 * VoyageTemplatePicker.js
 *
 * Modal for selecting a voyage template or starting fresh
 *
 * PHASE 3: Voyage Template Builder
 */

import React from 'react';
import { Ship, Clock, ChevronRight, Plus, X } from 'lucide-react';

const VoyageTemplatePicker = ({
  templates = [],
  onSelectTemplate,
  onStartFresh,
  onManageTemplates,
  onClose
}) => {
  // Format last used date
  const formatLastUsed = (dateString) => {
    if (!dateString) return 'Never used';
    const date = new Date(dateString);
    const days = Math.round((Date.now() - date.getTime()) / (1000 * 60 * 60 * 24));
    if (days === 0) return 'Today';
    if (days === 1) return 'Yesterday';
    if (days < 7) return `${days} days ago`;
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-end sm:items-center justify-center">
      <div className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Start Voyage</h2>
          <button
            onClick={onClose}
            className="p-2 -mr-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Template List */}
        <div className="overflow-y-auto max-h-[50vh] p-4 space-y-3">
          {templates.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Ship className="h-10 w-10 mx-auto mb-3 text-gray-300" />
              <p className="text-sm">No templates yet</p>
              <p className="text-xs mt-1">Start a voyage and save it as a template</p>
            </div>
          ) : (
            templates.map(template => (
              <button
                key={template.id}
                onClick={() => onSelectTemplate(template)}
                className="w-full bg-white rounded-xl border border-gray-200 p-4 hover:border-cyan-300 hover:shadow-md transition-all text-left"
              >
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-cyan-50 rounded-lg shrink-0">
                    <Ship className="h-5 w-5 text-cyan-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 truncate">
                      {template.name}
                    </p>
                    <p className="text-sm text-gray-500 truncate">
                      {template.from?.name} &rarr; {template.to?.name}
                    </p>
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {formatLastUsed(template.lastUsed)}
                      </span>
                      <span>Used {template.usageCount || 0}x</span>
                    </div>
                  </div>
                  <ChevronRight className="h-5 w-5 text-gray-300 shrink-0" />
                </div>
              </button>
            ))
          )}
        </div>

        {/* Actions */}
        <div className="p-4 border-t border-gray-200 space-y-2">
          <button
            onClick={onStartFresh}
            className="w-full flex items-center justify-center gap-2 py-3 bg-cyan-600 text-white font-medium rounded-xl hover:bg-cyan-700"
          >
            <Plus className="h-5 w-5" />
            Start Fresh
          </button>
          {templates.length > 0 && (
            <button
              onClick={onManageTemplates}
              className="w-full py-2.5 text-sm text-gray-600 hover:text-gray-900"
            >
              Manage Templates
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default VoyageTemplatePicker;
